import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

/**
 * THE keyboard-shortcut register, and the Help entry every page's ⋯ menu
 * carries ("Keyboard shortcuts…").
 *
 * The shortcuts a screen offers are written where they are IMPLEMENTED — the
 * items page's ↑↓/Enter handler, a form's ⌘↵ — and each of those surfaces
 * declares them here with `useScreenShortcuts` while it is mounted. The modal
 * then shows what is live RIGHT NOW: the screen's own sections first, the
 * ones that work everywhere last. A shortcut that is removed from a handler
 * and left in a static list is the failure this avoids.
 *
 *   useScreenShortcuts('Item form', [
 *     { keys: ['mod', 'enter'], label: 'Save' },
 *     { keys: ['esc'], label: 'Cancel' },
 *   ]);
 *
 * `keys` are TOKENS, not glyphs: 'mod' prints ⌘ on a Mac and Ctrl elsewhere,
 * and the rest ('enter', 'esc', 'up', 'shift', …) print the symbol the
 * platform's keyboards are labelled with. Anything unrecognised prints as
 * typed, so a plain letter is just 'N'.
 *
 * Sections are keyed by `title`: a screen that re-registers (its list depends
 * on state — "Save & add another" only exists for a new record) replaces its
 * own section rather than adding a second one.
 */

const ShortcutsContext = createContext(null);

// True for Apple keyboards, where the modifier is ⌘ rather than Ctrl. Read
// once: it cannot change under a running tab, and `userAgentData` is not
// everywhere yet.
const IS_APPLE = typeof navigator !== 'undefined'
	&& /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');

export const KEY_GLYPHS = {
	mod: IS_APPLE ? '⌘' : 'Ctrl',
	cmd: '⌘',
	ctrl: 'Ctrl',
	alt: IS_APPLE ? '⌥' : 'Alt',
	shift: '⇧',
	enter: '↵',
	esc: 'Esc',
	tab: '⇥',
	space: 'Space',
	up: '↑',
	down: '↓',
	left: '←',
	right: '→',
	backspace: '⌫',
	slash: '/',
};

/** One key token as the glyph a reader sees on their own keyboard. */
export const keyGlyph = token => KEY_GLYPHS[token] || String(token || '');

/**
 * The shortcuts that work on every screen. Kept here beside the register
 * rather than in the modal: the modal renders what it is given.
 */
export const GLOBAL_SHORTCUTS = Object.freeze([
	{ keys: ['mod', 'K'], label: 'Search everything — collections, cards, items, actions' },
	{ keys: ['mod', '\\'], label: 'Open the navigation, or fold it' },
	{ keys: ['esc'], label: 'Close the search, a modal, or the open menu' },
]);

export function ShortcutsProvider({ children }) {
	// Sections in registration order. Screens mount outside-in, so the page's
	// own section lands before anything a panel inside it adds — which is the
	// order a reader wants them in.
	const [sections, setSections] = useState([]);
	const [open, setOpen] = useState(false);

	const register = useCallback((title, items) => {
		setSections(prev => {
			const next = prev.filter(section => section.title !== title);
			next.push({ title, items });
			return next;
		});
	}, []);

	const unregister = useCallback((title) => {
		setSections(prev => prev.filter(section => section.title !== title));
	}, []);

	const openShortcuts = useCallback(() => setOpen(true), []);
	const closeShortcuts = useCallback(() => setOpen(false), []);

	const value = useMemo(() => ({
		sections,
		open,
		openShortcuts,
		closeShortcuts,
		register,
		unregister,
	}), [sections, open, openShortcuts, closeShortcuts, register, unregister]);

	return (
		<ShortcutsContext.Provider value={value}>
			{children}
		</ShortcutsContext.Provider>
	);
}

/**
 * The register, for the modal and for the ⋯ menus that open it. Optional on
 * purpose: a component rendered in a test without the provider still works,
 * it simply has no Help entry to offer.
 */
export function useShortcuts() {
	return useContext(ShortcutsContext);
}

/**
 * The Help entry itself — "Keyboard shortcuts…", in the taxonomy's `help`
 * group — for the surfaces that build their own ⋯ menu rather than letting
 * ContextBar draw it (the collection bar, whose kebab sits before its
 * primary button). Null without the provider, so a menu built in a bare test
 * simply has no Help entry rather than a dead one.
 */
export function useHelpAction() {
	const ctx = useContext(ShortcutsContext);
	const openShortcuts = ctx?.openShortcuts;
	return useMemo(() => (openShortcuts ? {
		key: 'help-shortcuts',
		group: 'help',
		glyph: 'shortcuts',
		label: 'Keyboard shortcuts…',
		onClick: openShortcuts,
	} : null), [openShortcuts]);
}

/**
 * Declare this screen's shortcuts for as long as it is mounted. `items` may
 * be rebuilt on every render — the registration is keyed by the section's
 * title and its contents, so an unchanged list does not re-register.
 */
export function useScreenShortcuts(title, items) {
	const ctx = useContext(ShortcutsContext);
	const registerRef = useRef(null);
	registerRef.current = ctx;
	// The list is the dependency, by value: callers build it inline.
	const signature = JSON.stringify(items || []);

	useEffect(() => {
		const register = registerRef.current;
		if (!register || !title) return undefined;
		register.register(title, JSON.parse(signature));
		return () => register.unregister(title);
	}, [title, signature]);
}
